/**
 * Servicios para la gestión de reportes de errores de archivos de participantes.
 *
 * Este módulo contiene todos los servicios necesarios para interactuar con la API
 * del backend en relación a los errores de carga masiva de participantes,
 * incluyendo operaciones de listado, resumen y exportación de errores.
 *
 * @author Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 * @date [Fecha de creación: 2025-08-06]
 * @updated [Fecha de última modificación: 2025-08-11]
 * @updatedBy Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 */

import { fetchWrapper } from "../../../fetchWrapper";

/**
 * Servicio para obtener la lista de errores de un archivo de carga masiva
 * @param {string} uploadId - ID del archivo de carga
 * @param {number} pageIndex - Índice de la página
 * @param {number} pageSize - Tamaño de la página
 * @param {Object} filters - Filtros adicionales (error_type, severity)
 * @returns {Object} Respuesta del servidor con la lista de errores
 */
export const ParticipantFileErrorsListService = async (uploadId, pageIndex, pageSize, filters = {}) => {
  try {
    let url = `${process.env.REACT_APP_BACKEND_URL}/participants/uploads/errors/${uploadId}/?page=${pageIndex}&pageSize=${pageSize}`;
    
    // Agregar filtros si existen
    if (filters.error_type) {
      url += `&error_type=${encodeURIComponent(filters.error_type)}`;
    }
    if (filters.severity) {
      url += `&severity=${encodeURIComponent(filters.severity)}`;
    }

    const result = await fetchWrapper(url, {
      method: "GET",
    });

    return result;
  } catch (error) {
    const message = "Error del servidor al realizar la solicitud";
    return { success: false, message: error.message || message };
  }
};

/**
 * Servicio para obtener el resumen de errores de un archivo de carga masiva
 * @param {string} uploadId - ID del archivo de carga
 * @returns {Object} Respuesta del servidor con el resumen de errores
 */
export const ParticipantFileErrorsSummaryService = async (uploadId) => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/participants/uploads/errors/summary/${uploadId}/`;

    const result = await fetchWrapper(url, {
      method: "GET",
    });

    return result;
  } catch (error) {
    const message = "Error del servidor al realizar la solicitud";
    return { success: false, message: error.message || message };
  }
};

/**
 * Servicio para exportar errores de un archivo de carga masiva a Excel
 * @param {string} uploadId - ID del archivo de carga
 * @param {Object} filters - Filtros adicionales (error_type, severity)
 * @returns {Blob} Archivo Excel para descarga
 */
export const ParticipantFileErrorsExportService = async (uploadId, filters = {}) => {
  try {
    let url = `${process.env.REACT_APP_BACKEND_URL}/participants/uploads/errors/${uploadId}/?export=true`;
    
    // Agregar filtros si existen
    if (filters.error_type) {
      url += `&error_type=${encodeURIComponent(filters.error_type)}`;
    }
    if (filters.severity) {
      url += `&severity=${encodeURIComponent(filters.severity)}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
      },
    });

    if (response.ok) {
      return await response.blob();
    } else {
      throw new Error(`Error del servidor al realizar la solicitud`);
    }
  } catch (error) {
    const message = "Error del servidor al realizar la solicitud";
    throw new Error(error.message || message);
  }
};